/**
 * Content / CMS API client. Thin wrappers over the shared `apiFetch` helper.
 */
import type {
  MediaAsset,
  MediaAssetInput,
  Page,
  PageInput,
  PageUpdate,
  PublicPage,
  PublicSermon,
  Sermon,
  SermonInput,
  SermonUpdate,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  BodyFormat,
  ContentStatus,
  MediaAsset,
  MediaAssetInput,
  MediaKind,
  Page,
  PageInput,
  PageUpdate,
  PublicPage,
  PublicSermon,
  Sermon,
  SermonInput,
  SermonUpdate,
} from '@fc/core'

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

// ------------------------------- Pages -------------------------------

export const listPages = () => apiFetch<Page[]>('/content/pages')

export const getPage = (id: string) => apiFetch<Page>(`/content/pages/id/${id}`)

export const createPage = (input: PageInput) =>
  apiFetch<Page>('/content/pages', { method: 'POST', ...json(input) })

export const updatePage = (id: string, patch: PageUpdate) =>
  apiFetch<Page>(`/content/pages/id/${id}`, { method: 'PATCH', ...json(patch) })

export const publishPage = (id: string) =>
  apiFetch<Page>(`/content/pages/id/${id}/publish`, { method: 'POST' })

export const unpublishPage = (id: string) =>
  apiFetch<Page>(`/content/pages/id/${id}/unpublish`, { method: 'POST' })

export const deletePage = (id: string) =>
  apiFetch<void>(`/content/pages/id/${id}`, { method: 'DELETE' })

// ------------------------------- Sermons -------------------------------

export const listSermons = () => apiFetch<Sermon[]>('/content/sermons')

export const getSermon = (id: string) => apiFetch<Sermon>(`/content/sermons/id/${id}`)

export const createSermon = (input: SermonInput) =>
  apiFetch<Sermon>('/content/sermons', { method: 'POST', ...json(input) })

export const updateSermon = (id: string, patch: SermonUpdate) =>
  apiFetch<Sermon>(`/content/sermons/id/${id}`, { method: 'PATCH', ...json(patch) })

export const publishSermon = (id: string) =>
  apiFetch<Sermon>(`/content/sermons/id/${id}/publish`, { method: 'POST' })

export const unpublishSermon = (id: string) =>
  apiFetch<Sermon>(`/content/sermons/id/${id}/unpublish`, { method: 'POST' })

export const deleteSermon = (id: string) =>
  apiFetch<void>(`/content/sermons/id/${id}`, { method: 'DELETE' })

// ------------------------------- Media -------------------------------

export const listMedia = () => apiFetch<MediaAsset[]>('/content/media')

export const createMedia = (input: MediaAssetInput) =>
  apiFetch<MediaAsset>('/content/media', { method: 'POST', ...json(input) })

export const deleteMedia = (id: string) =>
  apiFetch<void>(`/content/media/${id}`, { method: 'DELETE' })

// ------------------------------- Public -------------------------------

const orgQuery = (org: string) => `?org=${encodeURIComponent(org)}`

export const getPublicPage = (org: string, slug: string) =>
  apiFetch<PublicPage>(`/content/public/pages/${encodeURIComponent(slug)}${orgQuery(org)}`)

export const listPublicSermons = (org: string) =>
  apiFetch<PublicSermon[]>(`/content/public/sermons${orgQuery(org)}`)

export const getPublicSermon = (org: string, slug: string) =>
  apiFetch<PublicSermon>(`/content/public/sermons/${encodeURIComponent(slug)}${orgQuery(org)}`)
